<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { usePilotConfigStore } from 'dashboard/store/pilot/preferences';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePilot } from 'dashboard/composables/usePilot';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelSelector from './components/ModelSelector.vue';
import FeatureToggle from './components/FeatureToggle.vue';
import ModelDropdown from './components/ModelDropdown.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const pilotConfigStore = usePilotConfigStore();
const { uiFlags } = storeToRefs(pilotConfigStore);

const { isEnterprise, enterprisePlanName } = useConfig();
const { isOnChatwootCloud } = useAccount();
const { pilotEnabled } = usePilot();

const isLoading = computed(() => uiFlags.value.isFetching);

const shouldShowFeature = feature => {
  // Cloud will always see these features as long as pilot is enabled
  if (isOnChatwootCloud.value && pilotEnabled.value) {
    return true;
  }

  if (feature.enterprise) {
    // if the app is in enterprise mode, then we can show the feature
    // this is not the installation plan, but when the enterprise folder is missing
    return isEnterprise.value;
  }

  return true;
};

const isFeatureAccessible = feature => {
  // Cloud will always see these features as long as pilot is enabled
  if (isOnChatwootCloud.value && pilotEnabled.value) {
    return true;
  }

  if (feature.enterprise) {
    // plan is shown, but is it accessible?
    // This ensures that the instance has purchased the enterprise license, and only then we allow
    // access
    return isEnterprise.value && enterprisePlanName.value === 'enterprise';
  }

  return true;
};

const modelFeatures = computed(() => [
  {
    key: 'assistant',
    title: t('PILOT_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('PILOT_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
  },
  {
    key: 'copilot',
    title: t('PILOT_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('PILOT_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
  },
  {
    key: 'editor',
    title: t('PILOT_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('PILOT_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'knowledge_base_embedding',
    title: t('PILOT_SETTINGS.MODEL_CONFIG.KNOWLEDGE_BASE.TITLE'),
    description: t('PILOT_SETTINGS.MODEL_CONFIG.KNOWLEDGE_BASE.DESCRIPTION'),
  },
]);

const featureToggles = computed(() => [
  {
    key: 'audio_transcription',
    // 即将发布
    comingSoon: true,
  },
]);

// 帮助中心搜索功能 - 三个场景说明
const helpCenterSearchFeatures = computed(() => [
  {
    key: 'help_center_search_agent',
  },
  {
    key: 'help_center_search_portal',
  },
  {
    key: 'help_center_search_assistant',
  },
]);

const smartLearningFeatures = computed(() => [
  {
    key: 'faq_auto_extraction',
    modelSourceKey: 'knowledge_generation',
  },
  {
    key: 'label_suggestion',
    modelSourceKey: 'light_reasoning',
  },
]);

async function handleModelChange({ feature, model }) {
  try {
    await pilotConfigStore.updatePreferences({
      pilot_models: { [feature]: model },
    });
    useAlert(t('PILOT_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT_SETTINGS.API.ERROR'));
  }
}

// 智能学习功能的模型选择，映射到正确的配置 key
async function handleSmartLearningModelChange({ feature, model }) {
  // 智能学习功能的模型配置映射到独立模型组
  const modelKeyMap = {
    faq_auto_extraction: 'knowledge_generation',
    label_suggestion: 'light_reasoning',
  };

  const modelKey = modelKeyMap[feature] || feature;

  try {
    await pilotConfigStore.updatePreferences({
      pilot_models: { [modelKey]: model },
    });
    useAlert(t('PILOT_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT_SETTINGS.API.ERROR'));
  }
}

async function handleFeatureToggle({ feature, enabled }) {
  try {
    await pilotConfigStore.updatePreferences({
      pilot_features: { [feature]: enabled },
    });
    useAlert(t('PILOT_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT_SETTINGS.API.ERROR'));
  }
}

onMounted(() => {
  pilotConfigStore.fetch();
});
</script>

<template>
  <SettingsLayout>
    <BaseSettingsHeader
      :header-title="t('PILOT_SETTINGS.TITLE')"
      :description="t('PILOT_SETTINGS.DESCRIPTION')"
    />

    <div v-if="isLoading">
      <spinner />
    </div>

    <div v-else class="space-y-8">
      <!-- Model Configuration Section -->
      <SectionLayout v-if="false"
        :title="t('PILOT_SETTINGS.MODEL_CONFIG.TITLE')"
        :description="t('PILOT_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
      >
        <div class="grid gap-4">
          <ModelSelector
            v-for="feature in modelFeatures"
            :key="feature.key"
            :feature-key="feature.key"
            :title="feature.title"
            :description="feature.description"
            :is-allowed="true"
            :readonly="true"
            @change="handleModelChange"
          />
        </div>
      </SectionLayout>

      <!-- Smart Learning Section -->
      <SectionLayout
        :title="t('PILOT_SETTINGS.SMART_LEARNING.TITLE')"
        :description="t('PILOT_SETTINGS.SMART_LEARNING.DESCRIPTION')"
        with-border
      >
        <div class="grid gap-4">
          <FeatureToggle
            v-for="feature in smartLearningFeatures"
            :key="feature.key"
            :feature-key="feature.key"
            :model-source-key="feature.modelSourceKey"
            :is-allowed="true"
            :readonly-model="true"
            @change="handleFeatureToggle"
            @model-change="handleSmartLearningModelChange"
          />
        </div>
      </SectionLayout>

      <!-- Features Section -->
      <SectionLayout v-if="false"
        :title="t('PILOT_SETTINGS.FEATURES.TITLE')"
        :description="t('PILOT_SETTINGS.FEATURES.DESCRIPTION')"
        with-border
      >
        <div class="grid gap-4">
          <!-- 帮助中心搜索卡片（放在第一个） - 即将发布 -->
          <div
            class="p-4 rounded-xl border border-n-weak bg-n-solid-1 relative opacity-60"
          >
            <!-- 即将发布标签 -->
            <div
              class="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-medium text-n-slate-11 bg-n-alpha-2 rounded"
            >
              即将发布
            </div>

            <!-- 标题和描述 -->
            <div class="mb-4">
              <h4 class="text-sm font-medium text-n-slate-12">
                {{ t('PILOT_SETTINGS.HELP_CENTER_SEARCH.TITLE') }}
              </h4>
              <p class="text-sm text-n-slate-11 mt-0.5">
                {{ t('PILOT_SETTINGS.HELP_CENTER_SEARCH.DESCRIPTION') }}
              </p>
            </div>

            <!-- 总开关：启用文章向量化（禁用） -->
            <div
              class="flex items-center justify-between gap-4 pb-4 border-b border-n-weak"
            >
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{
                    t('PILOT_SETTINGS.HELP_CENTER_SEARCH.VECTORIZATION_TITLE')
                  }}
                </h4>
                <p class="text-sm text-n-slate-11 mt-0.5">
                  {{
                    t(
                      'PILOT_SETTINGS.HELP_CENTER_SEARCH.VECTORIZATION_DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <Switch :model-value="false" disabled />
            </div>

            <!-- 三个场景说明（树形结构，无开关） -->
            <div class="mt-4">
              <div class="text-sm text-n-slate-11 mb-2">
                {{ t('PILOT_SETTINGS.HELP_CENTER_SEARCH.SCENARIOS_LABEL') }}
              </div>
              <div
                v-for="(feature, index) in helpCenterSearchFeatures"
                :key="feature.key"
                class="flex items-start gap-2"
              >
                <!-- 树形线条 -->
                <div class="relative w-6 flex-shrink-0">
                  <div
                    v-if="index < helpCenterSearchFeatures.length - 1"
                    class="absolute left-3 top-3 w-3 h-[calc(100%+16px)] border-l-2 border-b-2 border-n-weak rounded-bl-md"
                  />
                  <div
                    v-else
                    class="absolute left-3 top-3 w-3 h-3 border-l-2 border-b-2 border-n-weak rounded-bl-md"
                  />
                </div>
                <!-- 场景说明 -->
                <div class="flex-1 py-2">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        `PILOT_SETTINGS.FEATURES.${feature.key.toUpperCase()}.TITLE`
                      )
                    }}
                  </h4>
                  <p class="text-sm text-n-slate-11 mt-0.5">
                    {{
                      t(
                        `PILOT_SETTINGS.FEATURES.${feature.key.toUpperCase()}.DESCRIPTION`
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>

            <!-- 模型选择器（只读） -->
            <div
              class="flex items-center justify-between gap-4 pt-4 mt-4 border-t border-n-weak"
            >
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{ t('PILOT_SETTINGS.HELP_CENTER_SEARCH.MODEL_TITLE') }}
                </h4>
                <p class="text-sm text-n-slate-11 mt-0.5">
                  {{ t('PILOT_SETTINGS.HELP_CENTER_SEARCH.MODEL_DESCRIPTION') }}
                </p>
              </div>
              <div class="flex-shrink-0">
                <ModelDropdown
                  feature-key="knowledge_base_embedding"
                  :readonly="true"
                />
              </div>
            </div>
          </div>

          <FeatureToggle
            v-for="feature in featureToggles.filter(shouldShowFeature)"
            :key="feature.key"
            :feature-key="feature.key"
            :is-allowed="isFeatureAccessible(feature)"
            :model-source-key="feature.modelSourceKey"
            :readonly-model="feature.readonlyModel"
            :coming-soon="feature.comingSoon"
            @change="handleFeatureToggle"
            @model-change="handleModelChange"
          />
        </div>
      </SectionLayout>
    </div>
  </SettingsLayout>
</template>
